import { useEffect, useMemo, useState } from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonSpinner,
  IonText,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { getSupabase } from '../../lib/supabase.js';

// T-282 — Tracciabilità COMPLETA per il preposto (per i controlli ASL).
// Il preposto è fail-closed su productions; i dati arrivano dall'edge
// `preposto-portal` (service-role, scoped al claim): le produzioni CONSEGNATE
// alla sua struttura, con il loro trace_token. Toccando una voce si apre la
// trace-page PUBBLICA (la vista ASL completa: ingredienti, lotti, fornitori,
// DDT d'origine — walk fino a 6 livelli).

interface Delivery {
  output_name: string;
  lp_code: string | null;
  trace_token: string | null;
  produced_at: string | null;
}

const TRACE_BASE =
  (import.meta.env.VITE_TRACE_BASE_URL as string | undefined) || 'https://trackeat.eu';

export default function PrepostoTracePage(): JSX.Element {
  const sb = useMemo(() => getSupabase(), []);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Delivery[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const { data, error } = await sb.functions.invoke('preposto-portal');
      if (!mounted) return;
      if (error) setToast('Errore nel caricamento della tracciabilità.');
      else setItems(((data as { deliveries?: Delivery[] } | null)?.deliveries) ?? []);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [sb]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/preposto" />
          </IonButtons>
          <IonTitle>Tracciabilità</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonText>
          <p style={{ margin: '0 4px 12px', color: 'var(--ion-color-medium)', fontSize: 13 }}>
            Per i controlli: tocca un prodotto ricevuto per vedere l'intera filiera (ingredienti,
            lotti, fornitori e DDT d'origine).
          </p>
        </IonText>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><IonSpinner /></div>
        ) : items.length === 0 ? (
          <IonNote style={{ padding: '0 4px' }}>
            Nessun prodotto ancora consegnato e tracciato per questa struttura.
          </IonNote>
        ) : (
          <IonList inset>
            {items.map((d, i) => (
              <IonItem key={`${d.trace_token ?? i}`}>
                <IonLabel>
                  {d.output_name}
                  <p>
                    {d.lp_code ? `Lotto ${d.lp_code}` : ''}
                    {d.produced_at ? ` · ${new Date(d.produced_at).toLocaleDateString('it-IT')}` : ''}
                  </p>
                </IonLabel>
                <IonButton
                  slot="end"
                  fill="outline"
                  size="small"
                  disabled={!d.trace_token}
                  onClick={() => d.trace_token && window.open(`${TRACE_BASE}/t/${d.trace_token}`, '_blank')}
                >
                  {d.trace_token ? 'Filiera' : 'n/d'}
                </IonButton>
              </IonItem>
            ))}
          </IonList>
        )}
        <IonToast isOpen={toast !== null} message={toast ?? ''} duration={2500} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
